/**
 * Client for the LUMEN backend (Cloudflare Worker)
 * All contribution paths go through Turnstile.
 */

import type { SceneFamily } from './types';

const DEFAULT_API = 'https://lumen-api.abhay-singh.workers.dev';

let API_BASE = (import.meta as any).env?.VITE_LUMEN_API || DEFAULT_API;

export function setApiBase(url: string) {
  API_BASE = url.replace(/\/$/, '');
}

function url(path: string) {
  return API_BASE + path;
}

export async function registerDevice(deviceKey: string, locale?: string, tz?: number) {
  try {
    await fetch(url('/api/register'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ device_key: deviceKey, locale, tz_offset: tz }),
    });
  } catch {}
}

export async function submitProbe(payload: {
  device_key: string;
  turnstileToken: string;
  phase: 'pre' | 'post';
  scene_family?: SceneFamily | null;
  trials: number;
  commission_errors: number;
  omission_errors: number;
  mean_rt: number;
  sd_rt: number;
  cv_rt: number;
  duration_ms: number;
  interrupted: boolean;
}) {
  const res = await fetch(url('/api/probe'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function submitExposure(payload: {
  device_key: string;
  turnstileToken: string;
  scene_family: SceneFamily;
  duration_s: number;
  ratings: { fascination: number; being_away: number; extent: number; compatibility: number };
  public_reflection?: string;
}) {
  const res = await fetch(url('/api/exposure'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      ratings: {
        fascination: payload.ratings.fascination,
        being_away: payload.ratings.being_away,
        extent: payload.ratings.extent,
        compatibility: payload.ratings.compatibility,
      },
    }),
  });
  return res.json();
}

export async function submitRestoration(payload: {
  device_key: string;
  turnstileToken: string;
  exposure_id: number;
  pre_id: number;
  post_id: number;
  deltas: { delta_commission: number; delta_cv: number; delta_mean_rt: number };
  lumen: number;
}) {
  const res = await fetch(url('/api/restoration'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function getCommons(embedding?: number[]) {
  let q = url('/api/commons');
  if (embedding) q += '?embedding=' + encodeURIComponent(JSON.stringify(embedding));
  const res = await fetch(q);
  return res.json();
}

export async function forget(deviceKey: string, turnstileToken: string) {
  const res = await fetch(url('/api/forget'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ device_key: deviceKey, turnstileToken }),
  });
  return res.json();
}

// Helper to render Turnstile (invisible or explicit)
export function renderTurnstile(container: HTMLElement, sitekey: string, callback: (token: string) => void) {
  // @ts-ignore
  if (window.turnstile) {
    // @ts-ignore
    window.turnstile.render(container, {
      sitekey,
      size: 'invisible',
      callback,
    });
  } else {
    // Fallback: show a simple note + auto "pass" for local dev
    container.innerHTML = `<div class="text-xs text-amber-400">Turnstile widget loading… (or running locally)</div>`;
    setTimeout(() => callback('local-dev-bypass-token'), 420);
  }
}
